// src/components/ChatInterface.tsx - Remove unused useEffect import
import React, { useState, useRef } from 'react';
import { Message, Attachment } from '../types/chat';
import { useAuth } from '../contexts/AuthContext';
import { analyticsApi } from '../services/api';

interface Props {
  messages: Message[];
  onSendMessage: (message: string, attachments?: Attachment[]) => void;
  loading: boolean;
  currentMessageId?: string;
  onNewChat?: () => void;
}

const ChatInterface: React.FC<Props> = ({
  messages,
  onSendMessage,
  loading,
  currentMessageId,
  onNewChat
}) => {
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, logout } = useAuth();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !loading) {
      // Process attachments if any
      let attachments: Attachment[] | undefined = undefined;
      if (selectedFiles.length > 0) {
        const { chatApi } = await import('../services/api');
        attachments = await chatApi.processFiles(selectedFiles);
      }

      onSendMessage(input.trim(), attachments);
      setInput('');
      setSelectedFiles([]);

      // Reset textarea height after sending message
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = '40px'; // Reset to minimum height
      }
    }
  };

  const renderMessageContent = (content: string, messageId?: string) => {
    const parts = content.split(/(```[\s\S]*?```)/);

    return parts.map((part, index) => {
      if (part.startsWith('```') && part.endsWith('```')) {
        // Skip code blocks - they are handled by CodeDisplay component in the execution panel
        // Rendering them here causes duplicates
        return null;
      } else {
        // Aggressive markdown cleaning and formatting
        const cleanInlineMarkdown = (text: string): string => {
          // 1. Remove header markdown (##, ###) - both at start and mid-line
          text = text.replace(/###\s*/g, '');
          text = text.replace(/##\s*/g, '');
          text = text.replace(/#\s+/g, '');

          // 2. Handle bold text **text** - convert to HTML
          text = text.replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold">$1</strong>');

          // 3. Handle italic/emphasis *text* or _text_
          text = text.replace(/\*([^*]+?)\*/g, '<em class="italic">$1</em>');
          text = text.replace(/_([^_]+?)_/g, '<em class="italic">$1</em>');

          // 4. Inline code `code`
          text = text.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-muted text-foreground rounded text-xs font-mono">$1</code>');

          // 5. Remove table pipes
          text = text.replace(/\|/g, ' ');

          // 6. Remove horizontal rules
          text = text.replace(/---+/g, '');
          text = text.replace(/___+/g, '');
          text = text.replace(/\*\*\*+/g, '');

          // 7. Clean up extra spaces
          text = text.replace(/\s{2,}/g, ' ');
          text = text.trim();

          return text;
        };

        const formatLine = (line: string): string => {
          if (!line.trim()) return '';

          const trimmedLine = line.trim();

          // Check for headers FIRST (before inline cleaning)
          if (trimmedLine.startsWith('### ')) {
            const content = cleanInlineMarkdown(trimmedLine.substring(4));
            return `<h3 class="text-base font-semibold mt-3 mb-2">${content}</h3>`;
          } else if (trimmedLine.startsWith('## ')) {
            const content = cleanInlineMarkdown(trimmedLine.substring(3));
            return `<h2 class="text-lg font-bold mt-4 mb-2">${content}</h2>`;
          } else if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('##')) {
            const content = cleanInlineMarkdown(trimmedLine.substring(2));
            return `<h1 class="text-xl font-bold mt-4 mb-3">${content}</h1>`;
          }

          // GENERIC LIST DETECTION: Check if line already contains list markers
          // This handles: "• text", "• • text", "• 1. text", "1. text", "- text", etc.
          const hasListMarker = /^[•\-*\d+.]\s/.test(trimmedLine);

          if (hasListMarker) {
            // Content already formatted - just render it as-is without adding extra markers
            return `<li class="ml-4">${cleanInlineMarkdown(trimmedLine)}</li>`;
          }

          // Legacy markdown list detection for clean content without markers
          if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
            const listContent = cleanInlineMarkdown(trimmedLine.substring(2));
            return `<li class="ml-4">• ${listContent}</li>`;
          }

          // Regular text - clean inline markdown
          return cleanInlineMarkdown(line);
        };

        const formattedLines = part.split('\n').map((line, lineIndex) => formatLine(line));

        return (
          <div
            key={index}
            className="text-[15px] leading-relaxed space-y-1"
            dangerouslySetInnerHTML={{ __html: formattedLines.join('<br />') }}
          />
        );
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCodeCopy = async (code: string, messageId: string, blockIndex: number) => {
    try {
      await navigator.clipboard.writeText(code);

      // Track the copy event
      await analyticsApi.trackCodeCopy({
        message_id: messageId,
        code_block_index: blockIndex,
        was_copied: true
      });

      console.log('Code copied and tracked!');
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      <div className="p-4 border-b border-border bg-card/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-sm">
              <span className="font-medium text-foreground">Welcome back, {user?.username}!</span>
              <span className="block text-xs text-muted-foreground">Developer</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onNewChat && messages.length > 0 && (
              <button
                onClick={onNewChat}
                className="px-3 py-1.5 text-sm bg-primary/10 text-primary hover:bg-primary/20 rounded-md transition-colors"
                title="Start a new conversation"
              >
                + New Chat
              </button>
            )}
            <button
              onClick={logout}
              className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex items-start space-x-3 ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
            {message.type === 'user' ? (
              <div className="w-8 h-8 rounded-full bg-primary-foreground/20 border border-primary-foreground/30 flex items-center justify-center text-base flex-shrink-0">
                👨‍💻
              </div>
            ) : (
              <img
                src="/cubemate-logo.png"
                alt="Cubemate"
                className="w-8 h-8 rounded-lg flex-shrink-0"
              />
            )}
            <div className={`max-w-3xl rounded-2xl p-4 ${
              message.type === 'user'
                ? 'bg-primary text-primary-foreground ml-12 border border-primary/20'
                : 'bg-card border border-border mr-12'
            }`}>
              <div>
                {renderMessageContent(
                  message.content,
                  message.type === 'system' ? message.id : undefined
                )}
              </div>
              {/* PDF export removed from ChatInterface - only available in ChatOnlyPage for research */}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="flex items-start space-x-3">
            <img
              src="/cubemate-logo.png"
              alt="Cubemate"
              className="w-8 h-8 rounded-lg flex-shrink-0 animate-spin"
              style={{ animationDuration: '3s' }}
            />
            <div className="bg-card border border-border rounded-lg p-3 mr-12">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Cubemate is thinking</span>
                <div className="flex space-x-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-border bg-card/50">
        {/* Attachment preview area */}
        {selectedFiles.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-muted border border-border rounded-lg text-sm">
                <span className="text-muted-foreground">📎</span>
                <span className="text-foreground">{file.name}</span>
                <span className="text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex space-x-2">
          {/* File attachment button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            accept=".txt,.py,.js,.json,.csv,.md,.html,.css,.java,.cpp,.c,.tsx,.ts"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="px-3 py-2 h-[40px] bg-muted hover:bg-muted/80 text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 transition-colors"
            title="Attach files"
          >
            📎
          </button>

          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // Auto-resize textarea
              if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!loading && input.trim()) {
                  handleSubmit(e as any);
                }
              }
            }}
            placeholder="Ask Cubemate anything... (Shift+Enter for new line)"
            disabled={loading}
            rows={1}
            className="flex-1 px-4 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors disabled:opacity-50 resize-none min-h-[40px] max-h-[200px] overflow-y-auto"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="px-4 py-2 h-[40px] self-end bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {loading ? '⏳' : '→'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;